import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dayShort } from '../lib/when';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import useMediaQuery from '../hooks/useMediaQuery';
import PageBar, { DESKTOP_QUERY } from '../components/PageBar';
import { useAuth } from '../components/AuthContext';
import { useToast } from '../components/ToastContext';
import { useOrg } from '../components/OrgContext';
import { DeskFrame, PhoneFrame } from '../components/AppFrame';
import NotBuiltYet from '../components/NotBuiltYet';
import { fmtVal } from './admin/adminUtils';
import { dueState, DueChip } from './home/parts';
/*
  **The pieces this screen and Cycle Details are both built from**, moved out on
  5 September 2026. The two screens are the same drawing grouped differently: an
  admin's by region, a manager's by rep. Before this they were two programs, and
  each of those was written twice, once per width.
*/
import {
  CYCLE_STATS, CycleStat, CycleStrip, GroupGrid, LocationList, PanelHead,
  RowButton, cardColumns, locationName,
} from './cycle/parts';
import { warehouseRepMap } from '../lib/assignments';
import { countRules } from '../lib/countRules';
import { cycleLineTotals } from '../lib/cycleTotals';
import { notify } from '../lib/notify';
import { logAudit } from '../hooks/useAudit';
import { save, fetchAll } from '../lib/save';
import * as reviewActions from './hub/reviewActions';
import ReviewHubPage from './hub/ReviewHubPage';
import OpenCycleDialog from './admin/OpenCycleDialog';
import CloseCycleDialog from './admin/CloseCycleDialog';
import AdminItemCatalog from './AdminItemCatalog';
import SiteUploadsPage from './admin/SiteUploadsPage';
import ReportsPage, { REPORT_KEYS } from './admin/ReportsPage';
import { menuFor } from '../lib/menu';
import AdminHome from './home/AdminHome';
import MyCounts from './cycle/MyCounts';
import UsersAccess from './admin/Settings/UsersAccess';
import SiteSettings from './admin/Settings/SiteSettings';
import Sites from './admin/Settings/Sites';
import Warehouses from './admin/Settings/Warehouses';
import JobQueuePage from './admin/Ordering/JobQueuePage';
import PlaceOrderPage from './admin/Ordering/PlaceOrderPage';
import ClientOrderingPage from './admin/Ordering/ClientOrderingPage';
import MovementProcessor from './admin/Movement/MovementProcessor';
import PageLoading from '../components/PageLoading';
import SaveFailedBar from '../components/SaveFailedBar';
import SystemShell from '../components/SystemShell';
import Appearance from './settings/Appearance';
import VendorMapping from './mapping/VendorMapping';
import ItemMapping from './mapping/ItemMapping';
import { PlaceMappingPage } from './mapping/PlaceMapping';

// Two group names the cycle screen uses that are not regions.
// UNASSIGNED is a location nobody has been given - the one that goes uncounted
// if nobody looks. NO_REGION is a location whose region was never filled in,
// which is a data problem, not a staffing one. They used to share the word
// "Unassigned" and mean two different things.
const UNASSIGNED = 'Unassigned';
const NO_REGION  = 'No Region';

// Two-tier navigation: tier 1 picks an AREA, tier 2 lists that area's pages.
// Icons are 24x24 stroke paths (Atrium style).
//
// There is no Admin area. Warehouses and the item catalog are what counts and
// orders are made of, not site setup, so they sit in Operations next to the
// counts that use them. Settings is for how the site itself is set up.
//
// Accounts and Referral Sources are deliberately absent. Counts are generated
// from warehouses now, and a warehouse carries its own referral source, so
// neither is something anyone edits here - referral sources are maintained in
// Claimsoft and arrive by import. Both tables are untouched and every screen
// that reads them still works; they simply have nowhere to be navigated to.
// **The menu moved out of this file on 5 September 2026.** It is in
// `src/lib/menu.jsx` now, with the manager's and the rep's, for the reason the
// owner gave for this job: a design change that has to be made in three places
// will be made in three places every time, forever. This screen had one of the
// six.


// Title blocks for the pages promoted out of Settings (they render no title of their own).
const ADMIN_PAGES = {
  users_access:     { title: 'Users & Access',  sub: '' },
  catalog:          { title: 'Item Catalog',     sub: 'Products, costs, and categories' },
  warehouses:       { title: 'Warehouses',       sub: 'Manage warehouses' },
  site_settings:    { title: 'Site Settings',    sub: '' },
  sites:            { title: 'Sites',            sub: '' },
  uploads:          { title: 'Branding & Uploads', sub: 'Logo and site branding' },
  appearance:       { title: 'Appearance',       sub: '' },
  vendor_mapping:   { title: 'Vendor Mapping',   sub: 'Every spelling your systems use for a vendor' },
  item_mapping:     { title: 'Item Mapping',     sub: 'Which ClaimSoft and Account Edge numbers belong to which product' },
  place_mapping:    { title: 'Place Mapping',    sub: 'One place, and what each system calls it' },
};

// The sections that have a name and a place in the menu and nothing behind them
// yet. **The per-section icon came out on 10 September 2026**: the one shape has
// one mark, a hammer, because a different picture for each unbuilt page reads as
// a status a person has to decode rather than as furniture. Same reason
// `EmptyScreen` has one mark for every empty screen in OnHand.
const SOON_SECTIONS = {
  shrink_analysis: {
    title: 'Shrink Analysis',
    subtitle: 'Where stock goes missing, and how much',
  },
  /*
    **The three the owner asked for on 3 October 2026**, in the menu before they
    were built. Vendor Mapping was built on the 3rd and Item Mapping on 5
    October, and both moved to the Setup area on 6 October, so Order & Routing
    Rules is the one left here. Each subtitle says what the page is FOR rather than when it is
    coming, because a date is a promise OnHand cannot keep.
  */
  order_rules: {
    title: 'Order & Routing Rules',
    subtitle: 'How an order is put together and where it goes',
  },
};

/*
  **One shape and one word, since 10 September 2026.** Job 25, from Group H's
  approved drawing.

  What was here was a big centred card with its own icon, its own badge reading
  "COMING SOON", and a sentence that said *"This section is on the roadmap and
  will be built out in the coming weeks. The menu item is in place now so
  navigation stays consistent."* That is two promises OnHand cannot keep,
  followed by the app explaining its own menu to the person reading it.

  **And it disagreed with the tab that opened it, in the same photograph**: the
  menu tab said "Soon" and the page said "COMING SOON". Three words for one idea
  across the app, two of them visible at once.
*/
function ComingSoon({ section }) {
  const meta = SOON_SECTIONS[section] || { title: 'Not built yet', subtitle: '' };
  return (
    <div>
      <div className="oh-ground-panel">
        <div className="oh-title" style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{meta.title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{meta.subtitle}</div>
      </div>
      <div style={{ marginTop: 18 }}>
        <NotBuiltYet title={meta.title} />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  // Which layout tree to mount. Same breakpoint the top bar uses, so the
  // mounted tree is always the one whose bar is on screen.
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  /*
    **How many region cards sit side by side**, from Group C's approved
    drawings: three on a desk and on a tablet on its side, two on a tablet
    upright, one on anything narrower. It was fixed at three, written straight
    into the markup, which put three cards across a phone 390 pixels wide: the
    word "Region" and the word "Done" landed on top of each other and read
    "REGIODNONE", and the region's name landed on top of its own percentage.

    It is counted here rather than left to the stylesheet because the locations
    of an opened region are laid UNDER THE ROW that card is in, so the number of
    cards in a row is something this screen has to know, not just something it
    draws.
  */
  const isTwoUp = useMediaQuery('(min-width: 768px)');
  const { profile } = useAuth();
  const toast = useToast();
  const { current: currentSite } = useOrg();
  const navigate = useNavigate();
  const [tab, setTabRaw]            = useState(() => {
    const saved = sessionStorage.getItem('adminTab');
    if (saved === 'todos') return 'overview';
    if (saved === 'branding') return 'uploads';
    // 'reports' was one page with an in-page report rail; reports are now pages.
    if (saved === 'reports') return REPORT_KEYS[0];
    // **Home is where the app opens now**, which is the whole reason it was
    // built: OnHand used to land on Inventory Counts, which is a page about
    // counting rather than a place to arrive. Somebody who left the app on
    // another page still comes back to it.
    return saved || 'home';
  });
  /*
    **`catalogActionsRef` was set and never read**, and with it eight actions the
    Item Catalog registered: two template downloads, the catalog CSV import, the
    barcode CSV import and four more. Nothing anywhere called any of them, so
    the catalog's own import was unreachable code that duplicated the live one on
    Branding & Uploads. Deleted 6 September 2026 at job 14, which exists to stop
    exactly that pair drifting apart.
  */
  const [catalogDropdownOpen, setCatalogDropdownOpen] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    if (!catalogDropdownOpen) return;
    const handler = () => setCatalogDropdownOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [catalogDropdownOpen]);
  function setTab(t) { sessionStorage.setItem('adminTab', t); setTabRaw(t); }
  const [cycle, setCycle]           = useState(null);
  const [progress, setProgress]     = useState([]);
  const [todos, setTodos]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [progressFilter, setProgressFilter] = useState('all');
  // The search line and the region picker Group C's drawing puts in the bar.
  const [locationSearch, setLocationSearch] = useState('');
  const [regionFilter, setRegionFilter]     = useState('');
  const [cycleForm, setCycleForm]   = useState({ name: '', quarter: 'Q1', year: new Date().getFullYear(), dueOn: '' });
  // The two Site Settings numbers that used to be read by nothing.
  const [rules, setRules]           = useState({ variancePct: null, notStartedDays: null });
  useEffect(() => { countRules().then(setRules); }, []);
  // Desktop region accordion: one region open at a time (null = none)
  const [expandedRegion, setExpandedRegion] = useState(null);
  const [valuations, setValuations]           = useState({}); // { count_id: dollar_value }
  const [lineCounts, setLineCounts]           = useState({}); // { count_id: { done, total } }
  const [cycleValue, setCycleValue]           = useState(null);

  /*
    **Opening the Review Hub is going to a page now**, rather than raising a
    dialog over whatever you were looking at. Everything that used to open the
    dialog on a particular queue changes the page instead, and the buttons that
    did it keep working.
  */
  /*
    **The last two things in OnHand that were not dialogs.** Opening a cycle was
    a form on an empty card and closing one was the grey box the browser draws.
    Both are dialogs now, on the shared shell, to Group E's drawings.
  */
  const [openCycleOpen, setOpenCycleOpen]   = useState(false);
  const [closeCycleOpen, setCloseCycleOpen] = useState(false);
  const [cycleBusy, setCycleBusy]           = useState(false);

  const [hubQueue, setHubQueue] = useState('counts');
  // Which row to open on, where something sent you here about one in
  // particular: "View count" on a submitted location means that count.
  const [hubPick, setHubPick]   = useState(null);
  function openReviewHub(opts = {}) {
    setHubQueue(opts.tab || 'counts');
    setHubPick(opts.countId || null);
    setTab('review_hub');
  }

  // Renders the action control for a count row on the Cycle Details region tables.
  // Unified sizing with Manager/Rep dashboards; admin also sees Approve + View Only.
  /*
    The button at the end of a location's row.

    **An admin may edit a count somebody else is in the middle of**, which is the
    one place this differs from the manager's version of the same row, and the
    gold tone is there to make somebody think before they do it.

    **A count already handed in is opened, not "reviewed".** Group C's approved
    drawing gives a submitted count and an approved count the same quiet button,
    View count, and only a count still to be done gets a filled one. Both still
    open the Review Hub with that count already expanded, which is what the
    button did when it said Review: the word changed, the place it goes did not.

    The button's SHAPE comes from `RowButton` in cycle/parts.jsx, shared with
    Cycle Details so the two cannot drift on it.
  */
  function renderCycleLocationAction(p) {
    if (p.status === 'submitted' || p.status === 'approved') {
      return (
        <RowButton tone="quiet" onClick={() => openReviewHub({ tab: 'counts', countId: p.id })}>
          View count
        </RowButton>
      );
    }
    // An admin CAN edit counts claimed by other reps. CountEntry shows a banner.
    const ownedByOther = p.rep_id && profile?.id && p.rep_id !== profile.id;
    if (ownedByOther) {
      return (
        <RowButton tone="caution"
          title={"Admin override, this will edit " + (p.rep?.full_name || 'a rep') + "'s open count"}
          onClick={() => navigate('/count/' + p.id)}>
          Override
        </RowButton>
      );
    }
    return (
      <RowButton onClick={() => navigate('/count/' + p.id)}>
        {p.status === 'not_started' ? 'Start count' : 'Continue count'}
      </RowButton>
    );
  }

  useEffect(() => { loadData(); }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  async function loadData() {
    setLoading(true);
    const [{ data: cycleData }, { data: todoData }] = await Promise.all([
      supabase.from('count_cycles').select('*').eq('status', 'open').single(),
      supabase.from('todos').select('*').eq('is_complete', false).order('created_at', { ascending: false }),
    ]);
    setCycle(cycleData);
    setTodos(todoData || []);

    if (cycleData) {
      /*
        **Four requests that waited on each other now go out together.**
        Corrected 8 September 2026. None of these four asks a question that
        depends on another one's answer, and they were being made one after the
        other, so the screen waited for the sum of them rather than for the
        slowest.
      */
      const [{ data: counts }, { data: reps }, whReps, totals] = await Promise.all([
        // **Every sheet, a page at a time** (the audit after the rebrand,
        // issue 22, 18 September 2026). It stopped at 500 with nothing to say
        // so; the open cycle has 274 today, and a site past 500 locations
        // would have lost the rest from every figure on this screen.
        fetchAll(() => supabase
          .from('inventory_counts')
          // `region_prefix` is the four letters a rep uses to talk about a place,
          // and Group C's drawing of an opened region puts it above the name.
          .select('id, status, submitted_at, approved_at, rep_id, warehouse:warehouses(id, name, display_name, region_prefix, region:regions(name))')
          .eq('cycle_id', cycleData.id)
          .order('status').order('id')),
        fetchAll(() => supabase.from('profiles').select('id, full_name').order('id')),
        warehouseRepMap(),
        /*
          **What each sheet is worth, and how much of it is filled in.**

          **This used to read every line in the cycle.** Rewritten 8 September
          2026: the open cycle holds 2,530 lines, a request brings back about a
          thousand of them, so this was three requests in a row and 2,530 rows
          crossing the wire for the sake of 23 numbers, with a blank page and a
          spinner on it the whole time. The database adds them up now and hands
          back one row per sheet.

          **The numbers are unchanged**, which was checked rather than assumed:
          the old sum and the new one were run side by side against the live
          database and agreed on all 23 sheets and on the cycle total of
          $10,377,690.37.

          **A blank is not a zero** (rule 9): a line nobody has touched is not a
          line counted as none, and that distinction now lives in one place in
          the database instead of two copies of it in the browser.
        */
        cycleLineTotals(cycleData.id),
      ]);
      const repMap = {};
      for (const r of reps || []) repMap[r.id] = r;
      setProgress((counts || []).map(c => ({
        ...c,
        rep: c.rep_id ? repMap[c.rep_id] : null,
        allReps: (whReps[c.warehouse?.id] || []).map(id => repMap[id]).filter(Boolean),
      })));

      /*
        **A read that did not work leaves the numbers alone.** Printing zeroes
        for a request that failed would say "nothing has been counted" about a
        cycle somebody has spent a week on.
      */
      if (!totals.failed) {
        setValuations(totals.valuations);
        setLineCounts(totals.lineCounts);
        setCycleValue(totals.cycleValue);
      }
    }
    setLoading(false);
  }

  // Opening a cycle is one call to the database, not a loop here. It creates
  // the cycle, a sheet for every warehouse, and every sheet's item list carried
  // forward from that warehouse's last count - or from the counts taken before
  // OnHand existed, the first time round. Quantities come across empty on
  // purpose: an empty box means nobody has counted it yet, and a rep who can
  // see last quarter's number is a rep who might write it down again.
  //
  // All of it or none of it. A closed tab can no longer leave a half-built
  // cycle behind, which mattered once this went from fifteen locations to
  // several hundred warehouses each carrying its own items.
  async function openCycle() {
    if (!cycleForm.name) { toast.error('Enter a cycle name'); return; }
    toast.info('Opening "' + cycleForm.name + '" and building the count sheets...');

    const { data, error } = await supabase.rpc('open_count_cycle', {
      p_name: cycleForm.name, p_quarter: cycleForm.quarter, p_year: cycleForm.year,
      p_due_on: cycleForm.dueOn || null,
    });
    if (error) { toast.error(error.message); return; }

    const carried = (data?.lines_from_last_count || 0) + (data?.lines_from_historical || 0);
    /*
      **The skipped count is said out loud on purpose.** Opening a cycle used to
      make a sheet for every location, including ones an administrator had
      confirmed closed for good, and now it does not. That is a number changing
      for a good reason, and a number that changes without explanation is what
      makes people stop trusting the rest of a screen.
    */
    toast.success(
      data.sheets + ' count sheets created'
      + (carried ? ', ' + carried + ' items carried forward' : '')
      + (data.locations_skipped
          ? '. ' + data.locations_skipped + ' closed or do-not-use location'
            + (data.locations_skipped === 1 ? '' : 's') + ' skipped'
          : '')
      + (data.unassigned_sheets
          ? '. ' + data.unassigned_sheets + ' are not assigned to anybody yet'
          : '')
    );
    loadData();
  }

  /*
    **The question moved to a dialog** on 5 September 2026, so this does the
    closing and nothing else. The grey box the browser drew said one sentence
    and could not show the two numbers that decide whether this is the right
    moment: how many sheets are not finished, and how many are waiting on the
    administrator's own approval.
  */
  async function closeCycle() {
    setCycleBusy(true);
    // Checked, rather than assumed. Closing a cycle stops everybody counting, so
    // being told it closed when it did not is the worst version of this job's
    // subject.
    if (!await save(toast, () => supabase.from('count_cycles')
      .update({ status: 'closed', closed_at: new Date().toISOString() }).eq('id', cycle.id),
      'the closed cycle')) { setCycleBusy(false); return; }
    setCycleBusy(false);
    setCloseCycleOpen(false);
    toast.success('Count cycle closed.');
    setCycle(null); setProgress([]); loadData();
  }

  // Approve: src/pages/hub/reviewActions.js (18 September 2026).
  const approveCount = countId => reviewActions.approveCount(hubCtx(), countId);

  // Send back: src/pages/hub/reviewActions.js (18 September 2026).
  const rejectCount = (sheet, reason, countMeta) => reviewActions.rejectCount(hubCtx(), sheet, reason, countMeta);
  // What the Review Hub's actions need from this page.
  const hubCtx = () => ({ supabase, toast, notify, logAudit, profile, setProgress, setTodos, loadData });

  /*
    Taking a picked-up location back off the rep who took it.

    **The owner asked for this on 3 September 2026**, in Group E's third round:
    "Being told is enough for the most part, but I would like the option to take
    it back and undo it if the rep did this in error."

    **What it does to the sheet is the part worth writing down.** It unassigns
    the rep and leaves the location for somebody else. The sheet stays, with
    anything they have counted still on it, which is what the app's own "release
    ownership" already does. The difference is that the rep is told: the app has
    never said a word to somebody whose location was taken back, so the first
    they knew was the location vanishing from their list.
  */
  async function releasePickedUpLocation(todo) {
    const meta = (() => { try { return JSON.parse(todo?.metadata || '{}') || {}; } catch { return {}; } })();
    const locationName = todo.warehouse?.name || meta.location_name || 'a location';
    if (todo.warehouse_id && cycle?.id) {
      if (!await save(toast, () => supabase.from('inventory_counts')
        .update({ rep_id: null })
        .eq('warehouse_id', todo.warehouse_id).eq('cycle_id', cycle.id),
        'taking the location back')) return;
    }
    if (!await save(toast, () => supabase.from('todos')
      .update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id),
      'the task')) return;
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    if (todo.rep_id) {
      await notify(toast, {
        // 'location_unassigned' is not a kind of notice the database accepts, so
        // every one was refused and the rep was never told (issue 12, proved on
        // the live database 18 September 2026). A location taken back is their
        // hold on it removed, which is what 'transfer_removed' already means.
        alert_type: 'transfer_removed',
        title: 'A location came off your list',
        message: locationName + ' has been taken back and is available for somebody else. Anything you counted is still on the sheet.',
        is_read: false,
        rep_id: todo.rep_id,
      });
    }
    await logAudit(profile, 'LOCATION_RELEASED', 'count',
      { target_type: 'warehouse', target_name: locationName, details: { todo_id: todo.id } });
    toast.success('Taken back. ' + locationName + ' is free for somebody else.');
  }

  async function completeTodo(todoId) {
    if (!await save(toast, () => supabase.from('todos')
      .update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todoId),
      'the completed task')) return;
    setTodos(prev => prev.filter(t => t.id !== todoId));
    toast.success('Task marked complete.');
  }

  async function approveEditRequest(todo) {
    let meta = {}; try { meta = JSON.parse(todo.metadata || '{}'); } catch {}
    if (todo.count_id) await supabase.from('inventory_counts').update({ status: 'in_progress' }).eq('id', todo.count_id);
    await supabase.from('todos').update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id);
    const { data: countData } = await supabase.from('inventory_counts').select('rep_id').eq('id', todo.count_id).single();
    await notify(toast, { alert_type: 'edit_approved', title: 'Edit request approved', message: 'Your request to reopen ' + (meta.location_name || '') + ' has been approved.', is_read: false, rep_id: countData?.rep_id, inventory_count_id: todo.count_id });
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    await logAudit(profile, 'COUNT_EDIT_APPROVED', 'count',
      { target_type: 'count', target_name: meta.location_name || 'a location',
        details: { count_id: todo.count_id } });
    toast.success('Edit request approved.');
    loadData();
  }

  async function denyEditRequest(todo, reason) {
    let meta = {}; try { meta = JSON.parse(todo.metadata || '{}'); } catch {}
    await supabase.from('todos').update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id);
    const { data: countData } = await supabase.from('inventory_counts').select('rep_id').eq('id', todo.count_id).single();
    await notify(toast, { alert_type: 'edit_denied', title: 'Edit request denied', message: 'Your request to reopen ' + (meta.location_name || '') + ' was denied.' + (reason ? ' Reason: ' + reason : ''), is_read: false, rep_id: countData?.rep_id, inventory_count_id: todo.count_id });
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    await logAudit(profile, 'COUNT_EDIT_DENIED', 'count',
      { target_type: 'count', target_name: meta.location_name || 'a location',
        details: { count_id: todo.count_id, reason: reason || null } });
    toast.info('Edit request denied.');
  }

  // Transfer and Deny on a transfer: src/pages/hub/reviewActions.js.
  const approveOwnershipTransfer = (todo, given) => reviewActions.approveOwnershipTransfer(hubCtx(), todo, given);
  const denyOwnershipTransfer = (todo, given, reason) => reviewActions.denyOwnershipTransfer(hubCtx(), todo, given, reason);

  async function confirmClosure(todo, cm, adminNotes) {
    const warehouseId  = cm.warehouseId;
    const repId        = cm.repId;
    const locationName = cm.locationName;
    // 1. Mark the location do-not-use. It is not deleted: the database refuses
    //    to delete a location that has ever been counted, and rightly - the
    //    count history outlives the place it was taken.
    if (warehouseId) {
      await supabase.from('warehouses').update({
        is_do_not_use: true,
        is_inactive:   true,
      }).eq('id', warehouseId);
    }
    // 2. Close the todo task
    await supabase.from('todos').update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id);
    // 3. Notify the rep
    if (repId) {
      await notify(toast, {
        alert_type: 'location_closed',
        title: 'Location Closure Confirmed',
        message: locationName + ' has been officially closed.' + (adminNotes ? ' Admin notes: ' + adminNotes : ''),
        is_read: false,
        rep_id: repId,
      });
    }
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    toast.success(locationName + ' has been closed and marked do not use.');
    loadData();
  }

  async function denyClosure(todo, cm, adminNotes) {
    const repId        = cm.repId;
    const locationName = cm.locationName;
    // Close the todo task
    await supabase.from('todos').update({ is_complete: true, completed_at: new Date().toISOString() }).eq('id', todo.id);
    // Notify the rep the closure was denied
    if (repId) {
      await notify(toast, {
        alert_type: 'closure_denied',
        title: 'Location Closure Denied',
        message: 'The closure request for ' + locationName + ' has been denied. The location stays open.' + (adminNotes ? ' Admin notes: ' + adminNotes : ''),
        is_read: false,
        rep_id: repId,
      });
    }
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    toast.info('Closure denied - rep has been notified.');
  }

  function toggleRegion(rName) {
    setExpandedRegion(prev => (prev === rName ? null : rName));
  }

  /*
    **A region on Home opens that region** (the audit after the rebrand, issue
    21, 18 September 2026). Every row went to Inventory Counts with nothing
    open, and "Nobody assigned" handed over a key that is not the real one.
    Now it opens that region's card, or the Nobody assigned card, with the
    filters cleared so everything in it shows.
  */
  const goToOpenCard = useRef(false);
  function openRegionFromHome(key) {
    if (key) {
      setLocationSearch(''); setRegionFilter(''); setProgressFilter('all');
      setExpandedRegion(key === '__unassigned' ? UNASSIGNED : key);
      goToOpenCard.current = true;
    }
    setTab('overview');
  }
  // **And the page goes to it.** The card opens where it sits in the grid,
  // which on a phone is below the bottom of the screen, so pressing the row
  // would have looked like nothing happened. The panel carries a top margin
  // for the bar, so it does not land underneath it.
  useEffect(() => {
    if (!goToOpenCard.current || tab !== 'overview') return;
    goToOpenCard.current = false;
    requestAnimationFrame(() => document.querySelector('.region-block')?.scrollIntoView({ block: 'start' }));
  }, [tab, expandedRegion]);

  const stats = {
    not_started: progress.filter(p => p.status === 'not_started').length,
    in_progress:  progress.filter(p => p.status === 'in_progress').length,
    submitted:    progress.filter(p => p.status === 'submitted').length,
    approved:     progress.filter(p => p.status === 'approved').length,
  };
  const total = progress.length;
  const pct   = total > 0 ? Math.round((stats.submitted + stats.approved) / total * 100) : 0;

  /*
    **Three filters now, and they narrow the same list.** Group C's drawing of
    this screen puts a search line and a region picker in the bar beside the
    status one, because an administrator looking for one location among 274 was
    scrolling for it.

    **The search reads a location's name AND its rep's**, which is what the
    drawing's own words say: "Search a location or a rep". Somebody chasing a
    person is as common as somebody chasing a place.
  */
  const searchText = locationSearch.trim().toLowerCase();
  const filteredProgress = progress.filter(p => {
    if (progressFilter !== 'all' && p.status !== progressFilter) return false;
    if (regionFilter && (p.warehouse?.region?.name || NO_REGION) !== regionFilter) return false;
    if (!searchText) return true;
    const names = (p.allReps || []).map(r => r?.full_name || '').join(' ');
    return ((p.warehouse?.name || '') + ' ' + names).toLowerCase().includes(searchText);
  });

  // Which group a sheet belongs to. A location nobody covers goes to Unassigned
  // whatever region it sits in - that is the whole point of the group. It shows
  // up there and nowhere else, so the cycle total still adds up.
  const groupOf = (p) =>
    (p.allReps?.length ? (p.warehouse?.region?.name || NO_REGION) : UNASSIGNED);

  const regionMap = {};
  filteredProgress.forEach(p => {
    const rName = groupOf(p);
    if (!regionMap[rName]) regionMap[rName] = [];
    regionMap[rName].push(p);
  });
  /*
    **The locations in a region come out in alphabetical order**, added 5
    September 2026 against Group C's approved drawing. They came out in whatever
    order the database handed them back, which for the Dallas region is 60 rows
    with no order at all: a person looking for one location by name had to read
    all sixty. Sorted on the name a person actually reads, which is the display
    name where a location has one, not the raw name with the territory prefix
    stuck on the front.
  */
  Object.keys(regionMap).forEach(rName => {
    regionMap[rName].sort((a, b) => locationName(a).localeCompare(locationName(b)));
  });

  // The region picker in the bar offers the real regions. `regionNames` below is
  // the list of GROUPS the cards are drawn from, and Unassigned is a group
  // rather than a region, so the two are not the same list.
  const realRegions = [...new Set(progress.map(p => p.warehouse?.region?.name).filter(Boolean))].sort();

  // Unassigned leads, because it is the group that needs somebody to act.
  const regionNames = [
    ...(progress.some(p => groupOf(p) === UNASSIGNED) ? [UNASSIGNED] : []),
    ...[...new Set(progress.filter(p => groupOf(p) !== UNASSIGNED).map(groupOf))].sort(),
  ];

  // The cards, in the shape the shared grid draws. A region that every current
  // filter has emptied still gets a card, showing nothing, rather than vanishing:
  // a group disappearing and a group with no matches look identical otherwise.
  const regionGroups = regionNames.map(rName => ({
    key: rName,
    kind: rName === UNASSIGNED ? 'Nobody assigned' : 'Region',
    name: rName,
    rows: regionMap[rName] || [],
  }));

  // **'settings' was a container key, not a page**, from when the settings
  // screens lived behind one combined component with its own tab strip. Nothing
  // renders it any more at either width: both layouts render the page the one
  // menu asked for. It is still mapped here because a browser that has the old
  // key remembered in its session should land somewhere sensible rather than on
  // a blank screen.
  /*
    **The Review Hub is a page now, not a dialog.** Same rows, same handlers,
    same guard on approving a count nobody has read; what changes is that it has
    the whole screen instead of a card in the middle of it, and below 1024 it is
    usable at all.
  */
  const hubProps = {
    cycle,
    lineCounts,
    // What OnHand itself knows was last counted at a location, for the Closures
    // panel: the owner asked for it beside what the rep says, because the two
    // are allowed to disagree and a disagreement is worth an admin's eye.
    allCounts: progress,
    valuations,
    queue: hubQueue,
    onQueueChange: setHubQueue,
    pick: hubPick,
    pendingCounts:      progress.filter(p => p.status === 'submitted'),
    editRequests:       todos.filter(t => t.todo_type === 'edit_request' && !t.is_complete),
    ownershipTransfers: todos.filter(t => t.todo_type === 'ownership_transfer' && !t.is_complete),
    closures:           todos.filter(t => t.todo_type === 'location_closure' && !t.is_complete),
    pickedUpLocations:  todos.filter(t => (!t.todo_type || t.todo_type === 'general') && !t.is_complete),
    onApproveCount:     approveCount,
    onRejectCount:      rejectCount,
    onApproveEdit:      approveEditRequest,
    onDenyEdit:         denyEditRequest,
    onConfirmClosure:   confirmClosure,
    onDenyClosure:      denyClosure,
    onMarkReviewed:     completeTodo,
    onTransferOwnership: approveOwnershipTransfer,
    onDenyTransfer:     denyOwnershipTransfer,
    onReleaseLocation:  releasePickedUpLocation,
  };

  const desktopTab = tab === 'settings' ? 'users_access' : tab;

  // The top bar is drawn from what this site has, not from what the app can do.
  // A module switched off in Site Settings is not hidden with CSS - its page is
  // never put in the bar, and an area left with no pages drops out entirely.
  // Absent means on, so a module added later needs no migration to appear.
  const visibleAreas = useMemo(() => menuFor('admin', {
    modules: currentSite?.modules || {},
    isGlobalAdmin: profile?.is_global_admin,
  }), [currentSite, profile]);

  // A tab can outlive the module that owned it - somebody switches Ordering off
  // while a colleague is standing on Replenishment, or a stored tab points at a
  // page this site no longer has. Land them on something that exists rather
  // than on a blank content area.
  const tabExists = visibleAreas.some(a => a.pages.some(p => p.key === desktopTab));
  useEffect(() => {
    // setTab writes sessionStorage and reads nothing, so it is deliberately not
    // a dependency - listing it would re-run this on every render.
    if (!tabExists && visibleAreas.length > 0) setTab(visibleAreas[0].pages[0].key);
  }, [tabExists, visibleAreas]);


  /*
    **The one line that hid eight pages, and it is gone.**

    It used to read:

      const tabletTab = tab === 'overview' ? 'overview'
                      : (tab === 'settings' || ADMIN_PAGES[tab] ? 'settings' : 'overview');

    Every page that was not Inventory Counts or a settings page became Inventory
    Counts below 1024. **Ordering's three, the four Reports and Shrink Analysis
    had no route at any width under a laptop**, and a person tapping one landed
    on the counts list with nothing saying why. Group D found it on three
    screens, Group F on all four Reports, Group G on the whole Ordering area and
    Group H on Shrink Analysis: one line, four groups, four separate findings.

    There is no clamp now. The held layout renders the same page the desk does.
  */
  /*
    **Home's numbers, built once and handed to the screen at both widths.**

    Every one is counted off the same `progress` and `todos` this screen already
    holds, rather than asked for again, so Home and Inventory Counts can never
    disagree about the same quarter.

    The five queues are exactly the five the Review Hub holds, in its order, so
    the badge on the card, the number in each row and the count in the Hub are
    one number counted one way.
  */
  const homeNeeds = useMemo(() => ([
    { key: 'counts',        n: progress.filter(p => p.status === 'submitted').length,
      title: 'Counts to review',    sub: 'Submitted and waiting on you',            tone: 'var(--info-ink)' },
    { key: 'edit_requests', n: todos.filter(t => t.todo_type === 'edit_request' && !t.is_complete).length,
      title: 'Edit requests',       sub: 'A rep wants a count reopened',            tone: 'var(--accent-ink)' },
    { key: 'closures',      n: todos.filter(t => t.todo_type === 'location_closure' && !t.is_complete).length,
      title: 'Closures',            sub: 'A location a rep says has shut',          tone: 'var(--critical-ink)' },
    { key: 'picked_up',     n: todos.filter(t => (!t.todo_type || t.todo_type === 'general') && !t.is_complete).length,
      title: 'Picked up',           sub: 'A rep started somewhere nobody was given', tone: 'var(--info-ink)' },
    { key: 'ownership',     n: todos.filter(t => t.todo_type === 'ownership_transfer' && !t.is_complete).length,
      title: 'Ownership transfers', sub: 'A rep wants a sheet another rep holds',   tone: 'var(--info-ink)' },
  ]), [progress, todos]);

  // The regions that are slipping, and the locations nobody is on. Slowest
  // first, three of them, which is what the drawing settles: a list of six
  // regions in full is the Inventory Counts screen, not Home.
  const homeRegions = useMemo(() => {
    const by = new Map();
    let unassigned = 0;
    for (const p of progress) {
      const g = groupOf(p);
      if (g === UNASSIGNED) { unassigned++; continue; }
      if (!by.has(g)) by.set(g, { name: g, total: 0, done: 0, notStarted: 0 });
      const r = by.get(g);
      r.total++;
      if (p.status === 'submitted' || p.status === 'approved') r.done++;
      if (p.status === 'not_started') r.notStarted++;
    }
    const all = [...by.values()].map(r => ({ ...r, pct: r.total ? Math.round(r.done / r.total * 100) : 0 }));
    all.sort((a, b) => a.pct - b.pct);
    return { rows: all.slice(0, 3), count: all.length, unassigned };
    // groupOf reads only its argument and the two constants above it, so it
    // is deliberately not a dependency.
  }, [progress]); // eslint-disable-line

  const heldTab = desktopTab;

  /*
    **The navigation**, rebrand step 4: the frames draw the top bar, the page
    pills and, on a phone, the tab bar from this, which is the same menu and the
    same page this screen already had. See src/components/AppFrame.jsx.
  */
  const nav = {
    areas: visibleAreas, activeKey: desktopTab, onSelect: setTab,
    userName: profile?.full_name, email: profile?.email,
    roleLabel: 'Administrator', onSignOut: signOut,
  };

  /*
    **A page loading keeps the shell**, rule 35 and Group H's approved drawing:
    the app is fine, the page is fine, it simply has not arrived. Until 10
    September 2026 this returned a spinner on a bare canvas, so for as long as
    the first read took, OnHand looked like it had nothing in it and offered no
    way anywhere.

    **It is `SystemShell` rather than a fourth copy of the bar**, so the menu
    here is the app's one menu and draws at both widths without this file
    knowing how. Same component the stopped screens use.
  */
  if (loading) return (
    <SystemShell>
      <PageLoading tall />
    </SystemShell>
  );

  /*
    **Inventory Counts, built once and rendered at both widths.**

    Until 5 September 2026 this screen existed twice: a desk version and a
    separate held version with its own cycle card, its own region cards and its
    own location cards, about 185 lines of them. **That is the duplication this
    whole job exists to remove**, in the owner's words: a change that has to be
    made in two places will be made in two places every time, forever, and these
    two had already drifted.

    **And the pieces it is built from are shared with Cycle Details**, which is
    the same drawing grouped by rep instead of by region. They live in
    `src/pages/cycle/parts.jsx`. Between them the two screens were four copies
    of one thing.

    Everything in it knows how to be narrow on its own. The cycle strip is a
    `PageBar`, which renders in place below 1024 rather than into the bar; the
    lists are `ListGrid`, which has three shapes; and the region cards are a grid
    that folds to one column.
  */
  const inventoryCountsView = cycle ? (
    <>
      {/*
        **The cycle strip, built to Group C's approved drawing on 5 September
        2026.** It replaces a card 190 pixels tall carrying a completion ring,
        the estimated value, four status chips and a Close Cycle button.

        **Rule 5: the live numbers sit on hairlines**, in one strip beside the
        name of the thing they describe, rather than in a card of their own. The
        drawing puts them in the bar, so the screen opens on the locations rather
        than on a summary of them, and this screen's job is the locations.

        **The status numbers still filter**, which is the one thing the card did
        that the drawing does not show: tapping one narrows the list to that
        status, and tapping it again clears it.

        **The four numbers change shape below 768, they do not wrap.** The
        drawings show a row of labelled numbers on a desk and a strip of four
        cells on anything narrower. Written after the row was photographed at 390
        and ran clean off the right-hand edge, with "68 Not started" the last
        thing on screen and the other three not there at all.

        **Rule 33: the primary act is always out**, so Close cycle is a button
        rather than something behind a menu, and Review Hub sits beside it
        because that is what the drawing shows.
      */}
      <PageBar
        twoRows
        metaFullWidth={!isTwoUp}
        /* **The overdue label, added 11 September 2026 with the brand rollout.**
           The guide's decision 2 turns every unstarted count neutral and gives a
           LATE count a red label of its own instead. Without this, turning the
           badges grey would have taken the only red off the screen where an
           admin actually acts on a late cycle, and put nothing back. It reads
           from the same cycle the Homes read, so the two can never disagree. */
        title={<>{cycle.name} <span className="pill pill-app" style={{ marginLeft: 8 }}>Cycle open</span>
          {dueState(cycle.due_on)?.tone === 'late' && (
            <span style={{ marginLeft: 8 }}><DueChip due={dueState(cycle.due_on)} /></span>
          )}</>}
        subtitle={isTwoUp
          ? 'Opened ' + dayShort(cycle.opened_at) + ' - ' + total + ' location' + (total !== 1 ? 's' : '')
          : pct + '% done - ' + total + ' location' + (total !== 1 ? 's' : '') + (cycleValue > 0 ? ' - ' + fmtVal(cycleValue) + ' est. value' : '')}
        meta={isTwoUp ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <CycleStat n={pct + '%'} label="Complete" dot="var(--info)" />
            <CycleStat n={cycleValue > 0 ? fmtVal(cycleValue) : 'Not counted yet'} label="Est. value" dot="var(--info)" />
            {CYCLE_STATS.map(c => (
              <CycleStat key={c.key} n={stats[c.key]} label={c.label} dot={c.dot}
                on={progressFilter === c.key}
                onClick={() => setProgressFilter(f => f === c.key ? 'all' : c.key)} />
            ))}
          </span>
        ) : (
          <CycleStrip stats={stats} filter={progressFilter} onFilter={setProgressFilter} />
        )}
        controls={
          <>
            <input className="tb-search" placeholder="Search a location or a rep"
              value={locationSearch} onChange={e => setLocationSearch(e.target.value)} />
            <select className={'tb-select' + (regionFilter ? ' set' : '')}
              value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
              <option value="">All regions</option>
              {realRegions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className={'tb-select' + (progressFilter !== 'all' ? ' set' : '')}
              value={progressFilter} onChange={e => setProgressFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
            </select>
          </>
        }
        actions={
          <>
            {/*
              **The blue moved off Close cycle, 12 September 2026**, on the
              owner's instruction after a design review put it first.

              Closing a cycle ends the quarter across every location and cannot
              be undone. It was the solid blue primary, which the brand guide
              defines as "the thing the person came to do", sitting in the
              permanent bar of a screen people open every day to read numbers.
              The most irreversible act in OnHand was drawn as the default.

              **The blue goes to Review Hub instead**, which is what an
              administrator actually comes to this screen to do, and which is
              where the counts waiting on them live. Rule 33 still holds: the
              primary act is out rather than behind a menu, and so is the
              quarterly one. Only their weight changed.
            */}
            <button className="btn btn-primary" onClick={() => openReviewHub({})}>Review Hub</button>
            <button className="btn btn-quiet" onClick={() => setCloseCycleOpen(true)}>Close cycle</button>
          </>
        }
      />

      {/* Region cards, an accordion: opening one closes any other, and its
          locations are laid under the ROW that card is in rather than under the
          card, so the cards beside it do not jump. */}
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10 }}><span className="oh-ground-tag">Regions</span></div>
      <GroupGrid
        groups={regionGroups}
        columns={cardColumns(isDesktop, isTwoUp)}
        openKey={expandedRegion}
        onToggle={toggleRegion}
        valueOf={rows => {
          const v = rows.reduce((sum, p) => sum + (valuations[p.id] || 0), 0);
          return v > 0 ? fmtVal(v) : null;
        }}
        renderPanel={g => (
          <div className="region-block" style={{ marginBottom: 14, scrollMarginTop: 96 }}>
            <PanelHead name={g.name === UNASSIGNED ? 'Nobody assigned' : g.name + ' region'} count={g.rows.length} />
            <div className="region-detail">
              <LocationList
                rows={g.rows}
                lineCounts={lineCounts}
                showRep
                dayOf={dayShort}
                action={renderCycleLocationAction}
                empty="No locations match the current filter."
              />
            </div>
          </div>
        )}
      />

      {progressFilter !== 'all' && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => setProgressFilter('all')}>Clear the filter, show all</button>
        </div>
      )}
    </>
  ) : null;

  // Desktop layout, on the shared desk frame (src/components/AppFrame.jsx). Visible at >=1024px viewport
  // via the .rep-desktop-only / .rep-mobile-only CSS pair (see index.css).
  /* MAIN - shared two-tier header: areas on tier 1, active area's pages on tier 2 */
  /* **No page row for an area holding one page.** The app's own rule and
  the approved shell's. Found by looking at Home on 5 September 2026:
  Home is one page, and the row drew a single tab under an area tab
  that already said the same word. */
  const desktopLayout = (
    <DeskFrame nav={nav}    >
          {/* **"That did not save", with the button to try it again.** Group H's
              approved bar, built 10 September 2026. It draws nothing at all
              unless a save has just failed for want of a connection, which is
              almost always. Under the page bar and above the work, so nothing a
              person is reading moves out from under them. */}
          <SaveFailedBar />

          {/* OVERVIEW */}
          {desktopTab === 'home' && (
            <AdminHome
              profile={profile} cycle={cycle} stats={stats} total={total} pct={pct}
              cycleValue={cycleValue} notStartedDays={rules.notStartedDays}
              regionRows={homeRegions.rows} regionTotal={homeRegions.count}
              unassignedCount={homeRegions.unassigned}
              needs={homeNeeds}
              onOpenCounts={() => (cycle ? setTab('overview') : setTab('overview'))}
              onCloseCycle={() => setCloseCycleOpen(true)}
              onOpenHub={key => openReviewHub(key ? { tab: key } : {})}
              onOpenRegion={openRegionFromHome}
              onOpenJobQueue={() => setTab('job_queue')}
            />
          )}

          {desktopTab === 'overview' && (
            <>
              {!cycle ? (
                /*
                  **The form went into a dialog, and what is left says what is
                  true.** Rule 9's family: an empty screen says what is missing
                  and what to do about it, and the act is a button rather than
                  four boxes and no explanation of what pressing them does.
                */
                <div className="card" style={{ maxWidth: 560 }}>
                  <div className="card-body" style={{ textAlign: 'center', padding: '40px 28px' }}>
                    <div className="oh-title" style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>No count is open</div>
                    <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.6 }}>
                      Opening one makes a count sheet for every active location, each carrying last
                      cycle's items with every quantity blank. Only one cycle can be open at a time.
                    </div>
                    <button className="btn btn-primary" style={{ marginTop: 18 }}
                      onClick={() => setOpenCycleOpen(true)}>Open a count cycle</button>
                  </div>
                </div>
              ) : (
                inventoryCountsView
              )}
            </>
          )}

          {REPORT_KEYS.includes(desktopTab) && <ReportsPage report={desktopTab} />}
          {desktopTab === 'mycounts' && <MyCounts cycle={cycle} profile={profile} navigate={navigate} />}
          {desktopTab === 'review_hub' && <ReviewHubPage {...hubProps} />}
          {desktopTab === 'job_queue' && <JobQueuePage />}
          {desktopTab === 'place_order' && <PlaceOrderPage />}
          {desktopTab === 'client_ordering' && <ClientOrderingPage />}
          {desktopTab === 'inventory_movement' && <MovementProcessor />}
          {SOON_SECTIONS[desktopTab] && <ComingSoon section={desktopTab} />}

          {/* Admin pages - promoted from Settings' internal rail to the header's page tier */}
          {/* No title block here any more - each page publishes its own name
              into the header's control tier through <PageBar>, which is what
              buys back the vertical space the extra tier costs. */}
          {ADMIN_PAGES[desktopTab] && (
            <div>
              {desktopTab === 'users_access' && <UsersAccess />}
              {desktopTab === 'site_settings' && <SiteSettings />}
              {desktopTab === 'sites'         && <Sites />}
              {desktopTab === 'catalog'  && <AdminItemCatalog />}
              {desktopTab === 'warehouses'       && <Warehouses />}
              {desktopTab === 'uploads'  && <SiteUploadsPage toast={toast} />}
              {desktopTab === 'appearance' && <Appearance />}
              {desktopTab === 'vendor_mapping' && <VendorMapping />}
              {desktopTab === 'item_mapping' && <ItemMapping />}
              {desktopTab === 'place_mapping' && <PlaceMappingPage />}
            </div>
          )}

    </DeskFrame>
  );

  // Tablet/mobile layout - sticky topbar, scrollable content area, fixed bottom nav.
  // Mirrors the pattern in RepDashboard and ManagerDashboard.
  // Tab content is wired in subsequent chunks (C to E); for now each tab shows a placeholder.
  /* Topbar - sticky logo + greeting */
  /* Floating glass header - identity only; Sign Out lives in the dock */
  /* Scrollable content - bottom padding leaves room for the fixed bottom nav */
  /* Detached glass dock - floats above content, which scrolls under it */
  const tabletLayout = (
    <PhoneFrame nav={nav}
    >
        {/* The same bar below 1024. A component added to one of a screen's two
            layout trees and not the other is missing with nothing saying so,
            which is exactly what happened to the connection bar on this day. */}
        <SaveFailedBar />

        <div>

          {heldTab === 'home' && (
            <AdminHome
              profile={profile} cycle={cycle} stats={stats} total={total} pct={pct}
              cycleValue={cycleValue} notStartedDays={rules.notStartedDays}
              regionRows={homeRegions.rows} regionTotal={homeRegions.count}
              unassignedCount={homeRegions.unassigned}
              needs={homeNeeds}
              onOpenCounts={() => (cycle ? setTab('overview') : setTab('overview'))}
              onCloseCycle={() => setCloseCycleOpen(true)}
              onOpenHub={key => openReviewHub(key ? { tab: key } : {})}
              onOpenRegion={openRegionFromHome}
              onOpenJobQueue={() => setTab('job_queue')}
            />
          )}

          {heldTab === 'overview' && (
            !cycle ? (
              /*
                **The same empty state the desk shows, and the same dialog.**
                There were two cycle forms, one here and one on the desk, which
                is the drift this project keeps finding: the desk's gained the
                "finish by" date on 5 September and this one had to be changed
                separately on the same day.
              */
              <div className="card" style={{ padding: '32px 20px', textAlign: 'center' }}>
                <div className="oh-title" style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>No count is open</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.6 }}>
                  Opening one makes a count sheet for every active location, each carrying last
                  cycle's items with every quantity blank. Only one cycle can be open at a time.
                </div>
                <button className="btn btn-primary" style={{ marginTop: 18, width: '100%', minHeight: 44 }}
                  onClick={() => setOpenCycleOpen(true)}>Open a count cycle</button>
              </div>
            ) : (
              /* **The same screen the desk renders**, since 5 September 2026.
                 There used to be a second version here with its own cycle card,
                 its own region cards and its own location cards, about 185 lines
                 of them, and it had already drifted from the desk's. */
              inventoryCountsView
            )
          )}


          {/* **The eight pages that had no route below 1024.** Each is the same
              self-contained component the desk renders, so there is no second
              version of any of them to drift. */}
          {REPORT_KEYS.includes(heldTab) && <ReportsPage report={heldTab} />}
          {heldTab === 'mycounts' && <MyCounts cycle={cycle} profile={profile} navigate={navigate} />}
          {/* **The Review Hub below 1024 as well.** Its whole reason for
              becoming a page is that the dialog was unusable on a phone, so a
              page that only exists on a desk would have missed the point
              entirely. Caught by photographing it at 390 and finding an empty
              screen inside a working shell. */}
          {heldTab === 'review_hub' && <ReviewHubPage {...hubProps} />}
          {heldTab === 'job_queue' && <JobQueuePage />}
          {heldTab === 'place_order' && <PlaceOrderPage />}
          {heldTab === 'client_ordering' && <ClientOrderingPage />}
          {heldTab === 'inventory_movement' && <MovementProcessor />}
          {SOON_SECTIONS[heldTab] && <ComingSoon section={heldTab} />}

          {/*
            **The settings pages below 1024 are the same pages the desk renders**,
            picked by the one menu, since 5 September 2026.

            Until then they went through a second component that carried its own
            tab strip: a menu inside a page, under the menu in the bar and the
            menu in the dock. That is the exact fault "One shell, one menu, three
            homes" was written to kill, and it survived here.

            **It was doing visible harm, found by photographing the screen.** The
            bar said "Branding & Uploads" and the page underneath showed Users &
            Access, because that second menu had no entry for uploads below 1024
            and quietly fell back to its first tab. Sites had no entry there at
            all. So two pages the menu offered could not be opened, and neither
            said so.
          */}
          {heldTab === 'users_access' && <UsersAccess />}
          {heldTab === 'site_settings' && <SiteSettings />}
          {heldTab === 'sites'         && <Sites />}
          {heldTab === 'catalog'  && <AdminItemCatalog mobile />}
          {heldTab === 'warehouses'    && <Warehouses />}
          {heldTab === 'uploads'  && <SiteUploadsPage toast={toast} />}
          {heldTab === 'vendor_mapping' && <VendorMapping />}
          {heldTab === 'item_mapping' && <ItemMapping />}
          {heldTab === 'place_mapping' && <PlaceMappingPage />}
          {heldTab === 'appearance' && <Appearance />}

        </div>
    </PhoneFrame>
  );

  return (
    <>
      {/* Only the tree for the current layout is mounted. Hiding one with CSS
          alone left it rendering: its pages still portalled their controls into
          the visible top bar, so any page reachable from both trees put its
          control band in there twice (Settings, and the manager's My Counts).
          Same query PageBar tests, imported rather than retyped. */}
      <div className="rep-desktop-only">{isDesktop && desktopLayout}</div>
      <div className="rep-mobile-only">{!isDesktop && tabletLayout}</div>

      {/* **The two dialogs that used to be a form and a browser prompt.** They
          live outside both trees so they open over the screen at any width, the
          way every other dialog in OnHand does since rule 34. */}
      {openCycleOpen && (
        <OpenCycleDialog
          form={cycleForm}
          busy={cycleBusy}
          onChange={(k, v) => setCycleForm(p => ({ ...p, [k]: v }))}
          onOpen={async () => { setCycleBusy(true); await openCycle(); setCycleBusy(false); setOpenCycleOpen(false); }}
          onClose={() => setOpenCycleOpen(false)}
        />
      )}
      {closeCycleOpen && (
        <CloseCycleDialog
          cycle={cycle}
          progress={progress}
          busy={cycleBusy}
          onConfirm={closeCycle}
          onClose={() => setCloseCycleOpen(false)}
        />
      )}
    </>
  );
}
